import { useState, useEffect } from "react";
import PanelDrawer from "../components/panel/panel-drawer";
import PanelHeader from "../components/panel/panel-header";
import PanelTable from "../components/panel/panel-table";
import { Panel } from "../types/panel.types";
import { useGetOrganizationClientsQuery } from "src/pages/client-management/endpoints/client.endpoints";
import { useCreatePanelMutation, useUpdatePanelFieldMutation, useUpdatePanelMutation, useGenerateSignedUploadUrlMutation } from "../endpoints/panel.endpoints";
import notificationService from "src/services/notification/notification.service";
import { useDispatch } from "react-redux";
import UploadStatusBar, { UploadStatus } from "src/components/upload-status-bar/upload-status-bar";
import { limitCSVToRows } from "../utils/csv-utils";

const PanelsTab = () => {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel | undefined>(undefined);
  const [activePanel, setActivePanel] = useState<Panel | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const dispatch = useDispatch();

  const { data: clients } = useGetOrganizationClientsQuery();
  const [createPanel, { isLoading: isCreatingPanel, isSuccess: isCreatedPanel }] = useCreatePanelMutation();
  const [updatePanel, { isLoading: isUpdatingPanel, isSuccess: isUpdatedPanel }] = useUpdatePanelMutation();
  const [updatePanelField, { isLoading: isUpdatingPanelField }] = useUpdatePanelFieldMutation();
  const [generateSignedUploadUrl] = useGenerateSignedUploadUrlMutation();

  useEffect(() => {
    if (panel) {
      setActivePanel(panel);
    } else {
      setActivePanel(null);
    }
  }, [panel]);

  const handleOpenChange = (panel?: Panel) => {
    setPanel(panel);
    setOpen(!open);
  };

  const handleCreatePanelClick = () => {
    setPanel(undefined);
    setActivePanel(null);
    setOpen(!open);
  };


  const uploadFileToS3 = async(file: File, signedUrl: string): Promise<void> => {
    if (typeof signedUrl !== 'string' || !signedUrl) {
      throw new Error('Invalid signed URL received from server');
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded * 100) / e.total);
          // Map progress from 30-100% (since we're at 30% when this starts)
          const mappedProgress = 30 + (percent * 0.7);
          setUploadStatus({
            fileName: file.name,
            progress: mappedProgress,
            status: 'uploading',
          });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadStatus({
            fileName: file.name,
            progress: 100,
            status: 'success',
          });
          resolve();
        } else {
          const errorText = xhr.responseText || `Upload failed with status ${xhr.status}`;
          let errorMessage = errorText;

          // Parse XML error for better error message
          if (errorText.includes('<Code>')) {
            const codeMatch = errorText.match(/<Code>([^<]+)<\/Code>/);
            const messageMatch = errorText.match(/<Message>([^<]+)<\/Message>/);
            if (codeMatch && messageMatch) {
              errorMessage = `${codeMatch[1]}: ${messageMatch[1]}`;
            }
          }

          setUploadStatus({
            fileName: file.name,
            progress: 0,
            status: 'error',
            error: errorMessage,
          });
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        setUploadStatus({
          fileName: file.name,
          progress: 0,
          status: 'error',
          error: 'Network error during upload',
        });
        reject(new Error('Network error during upload'));
      });

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', 'text/csv');
      xhr.send(file);
    });
  };

  const handleCreatePanelFromFile = async(file: File) => {
    try {
      if (!file) return;

      // Initialize upload status
      setUploadStatus({
        fileName: file.name,
        progress: 0,
        status: 'uploading',
      });

      // Step 0: Limit CSV to first 500 rows
      setUploadStatus({
        fileName: file.name,
        progress: 5,
        status: 'uploading',
      });

      const limitedFile = await limitCSVToRows(file, 500);

      // Step 1: Create panel with limited file
      setUploadStatus({
        fileName: file.name,
        progress: 10,
        status: 'uploading',
      });

      const createdPanel = await createPanel({
        name: file?.name.split('.')[0] ?? 'Untitled Panel',
        file: limitedFile,
        client: undefined,
      }).unwrap();

      // Step 2: Get signed URL
      setUploadStatus({
        fileName: file.name,
        progress: 20,
        status: 'uploading',
      });

      let signedUrl: string;
      try {
        signedUrl = await generateSignedUploadUrl({
          panel_id: createdPanel.id,
          filename: limitedFile.name, // Use limited file name
        }).unwrap();
      } catch (error) {
        console.error('Error generating signed URL:', error);
        throw new Error(`Failed to generate signed URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      // Step 3: Upload limited file directly to S3
      setUploadStatus({
        fileName: file.name,
        progress: 30,
        status: 'uploading',
      });

      await uploadFileToS3(file, signedUrl);

      // Step 4: Update panel to mark upload as complete (if needed)
      // The backend should handle linking the uploaded file to the panel
      // We might need to refresh the panel data or the backend handles it automatically

      return createdPanel;
    } catch (error) {
      setUploadStatus({
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to upload file',
      });
      notificationService.handleError(error, dispatch, 'Failed to create panel');
      console.error('Failed to create panel:', error);
      throw error;
    }
  };

  const handleFileChange = async(file: File | null) => {
    if (!file) return;
    try {
      const createdPanel = await handleCreatePanelFromFile(file);
      if (createdPanel) {
        setActivePanel(createdPanel);
      }
    } catch (error) {
      // Error handling is done in handleCreatePanelFromFile
    }
  };

  const handlePanelChange = (field: string, value: any) => {
    try {
      if (!activePanel) return;
      void updatePanel({ id: activePanel?.id ?? '', field, value });
      setActivePanel(prev => ({
        ...prev!,
        [field]: value,
      }));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to update panel');
      console.error('Failed to update panel:', error);
    }
  };

  const handleColumnsChange = (id: string, field: string, value: any) => {
    try {
      void updatePanelField({ id, field, value });

      const updatedFields = (activePanel?.fields ?? []).map(fieldObj => {
        if (fieldObj.id === id) {
          return {
            ...fieldObj,
            [field]: value,
          };
        }
        return fieldObj;
      });

      setActivePanel(prev => ({
        ...prev!,
        fields: updatedFields,
      }));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to update panel fields');
      console.error('Failed to update panel fields:', error);
    }
  };

  return (
    <div className="">
      <PanelDrawer
        open={open}
        panel={panel}
        activePanel={activePanel}
        clients={clients ?? []}
        onOpenChange={handleOpenChange}
        onFileChange={handleFileChange}
        onPanelChange={handlePanelChange}
        onColumnsChange={handleColumnsChange}
      />
      <PanelHeader onCreatePanel={handleCreatePanelClick} />
      <PanelTable
        clients={clients ?? []}
        onOpenChange={handleOpenChange}
      />
      <UploadStatusBar
        uploadStatus={uploadStatus}
        onDismiss={() => setUploadStatus(null)}
      />
    </div>
  );
};

export default PanelsTab;
